class WebRTCService {
  constructor() {
    this.ws = null;
    this.peerConnection = null;
    this.dataChannel = null;
    this.roomId = null;
    this.peerId = this.generateId();
    this.isInitiator = false;
    this.onMessageCallback = null;
    this.onConnectionStateChange = null;
    
    this.configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };
  }

  generateId() {
    return `peer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  connect(serverUrl = 'ws://localhost:3001') {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(serverUrl);

        this.ws.onopen = () => {
          console.log('✅ Connected to signaling server');
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleSignalingMessage(JSON.parse(event.data));
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };

        this.ws.onclose = () => {
          console.log('WebSocket connection closed');
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  async createRoom(roomId, peerName = 'Sender') {
    this.roomId = roomId;
    this.isInitiator = true;

    this.send({
      type: 'create-room',
      roomId,
      peerId: this.peerId,
      peerName
    });

    await this.setupPeerConnection();
  }

  async joinRoom(roomId, peerName = 'Receiver') {
    this.roomId = roomId;
    this.isInitiator = false;

    this.send({
      type: 'join-room',
      roomId,
      peerId: this.peerId,
      peerName
    });

    await this.setupPeerConnection();
  }

  async setupPeerConnection() {
    this.peerConnection = new RTCPeerConnection(this.configuration);

    // Set up data channel
    if (this.isInitiator) {
      this.dataChannel = this.peerConnection.createDataChannel('fileTransfer', {
        ordered: true
      });
      this.setupDataChannel();
    } else {
      this.peerConnection.ondatachannel = (event) => {
        this.dataChannel = event.channel;
        this.setupDataChannel();
      };
    }

    // ICE candidate handling
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.send({
          type: 'ice-candidate',
          roomId: this.roomId,
          candidate: event.candidate,
          targetPeerId: this.targetPeerId
        });
      }
    };

    // Connection state monitoring
    this.peerConnection.onconnectionstatechange = () => {
      console.log('Connection state:', this.peerConnection.connectionState);
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange(this.peerConnection.connectionState);
      }
    };
  }

  setupDataChannel() {
    this.dataChannel.binaryType = 'arraybuffer';

    this.dataChannel.onopen = () => {
      console.log('✅ Data channel opened');
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange('connected');
      }
    };

    this.dataChannel.onclose = () => {
      console.log('Data channel closed');
    };

    this.dataChannel.onmessage = (event) => {
      if (this.onMessageCallback) {
        this.onMessageCallback(event.data);
      }
    };

    this.dataChannel.onerror = (error) => {
      console.error('Data channel error:', error);
    };
  }

  async handleSignalingMessage(data) {
    console.log('Received signaling message:', data.type);

    switch (data.type) {
      case 'room-created':
      case 'room-joined':
        if (this.isInitiator && data.type === 'room-joined') {
          await this.createOffer();
        }
        break;

      case 'peer-joined':
        this.targetPeerId = data.peerId;
        if (this.isInitiator) {
          await this.createOffer();
        }
        break;

      case 'offer':
        await this.handleOffer(data.offer);
        this.targetPeerId = data.peerId;
        break;

      case 'answer':
        await this.handleAnswer(data.answer);
        break;

      case 'ice-candidate':
        await this.handleIceCandidate(data.candidate);
        break;

      case 'peer-left':
      case 'peer-disconnected':
        console.log('Peer disconnected');
        if (this.onConnectionStateChange) {
          this.onConnectionStateChange('disconnected');
        }
        break;

      case 'error':
        console.error('Signaling error:', data.message);
        break;

      default:
        console.log('Unknown message type:', data.type);
    }
  }

  async createOffer() {
    try {
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      this.send({
        type: 'offer',
        roomId: this.roomId,
        peerId: this.peerId,
        targetPeerId: this.targetPeerId,
        offer
      });
    } catch (error) {
      console.error('Error creating offer:', error);
    }
  }

  async handleOffer(offer) {
    try {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      this.send({
        type: 'answer',
        roomId: this.roomId,
        peerId: this.peerId,
        targetPeerId: this.targetPeerId,
        answer
      });
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  }

  async handleAnswer(answer) {
    try {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  }

  async handleIceCandidate(candidate) {
    try {
      await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  sendData(data) {
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      this.dataChannel.send(data);
      return true;
    }
    return false;
  }

  onMessage(callback) {
    this.onMessageCallback = callback;
  }

  onConnectionChange(callback) {
    this.onConnectionStateChange = callback;
  }

  disconnect() {
    if (this.dataChannel) {
      this.dataChannel.close();
    }
    if (this.peerConnection) {
      this.peerConnection.close();
    }
    if (this.ws) {
      this.send({
        type: 'leave-room',
        roomId: this.roomId,
        peerId: this.peerId
      });
      this.ws.close();
    }
  }
}

export default WebRTCService;
